/**
 * Push notifications bridge (iOS, APNs).
 *
 * `@capacitor/push-notifications` is **not** a dependency of apps/web on
 * purpose: this bundle is served to browsers, and the plugin's web
 * implementation would drag a Firebase path into a build that has no use for
 * it. Inside the shell the native runtime injects `window.Capacitor.Plugins`
 * before our bundle runs, so the plugin is reachable by name with a feature
 * check — and on the web every function here is a quiet no-op.
 *
 * Token lifecycle: APNs rotates device tokens (reinstall, restore, OS upgrade),
 * so the app re-registers on every cold start where permission is already
 * granted, and the server upserts by token. The last token is remembered
 * locally purely so sign-out can DELETE the right row.
 */
import { App as CapApp } from '@capacitor/app';
import { devicesApi } from '@/lib/api';
import { isNative, nativePlatform } from './index';

export type PushPermission = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';

/** What the caller can act on: the plugin is only there inside the shell. */
export type PushState = PushPermission | 'unsupported';

export interface PushNotificationPayload {
  title?: string;
  body?: string;
  data?: Record<string, any>;
}

const TOKEN_KEY = 'sweetspot_push_token';

type Unsubscribe = () => void;

function pushPlugin(): any | null {
  if (!isNative() || typeof window === 'undefined') return null;
  const plugins = (window as any).Capacitor?.Plugins;
  const plugin = plugins?.PushNotifications;
  return plugin && typeof plugin.register === 'function' ? plugin : null;
}

/** True when this build is running somewhere that can actually receive a push. */
export function isPushSupported(): boolean {
  return pushPlugin() !== null;
}

export async function pushPermission(): Promise<PushState> {
  const plugin = pushPlugin();
  if (!plugin) return 'unsupported';
  try {
    const result = await plugin.checkPermissions();
    return (result?.receive as PushPermission) ?? 'prompt';
  } catch {
    return 'unsupported';
  }
}

let registrationListenersAttached = false;
let registrationInFlight: Promise<string | null> | null = null;

/**
 * Attach the token listeners exactly once.
 *
 * `register()` is fire-and-forget — the token comes back later on the
 * `registration` event — so the listener has to exist before the call, and
 * attaching it twice would send the registration twice.
 */
async function attachRegistrationListeners(plugin: any): Promise<void> {
  if (registrationListenersAttached) return;
  registrationListenersAttached = true;

  await plugin.addListener('registration', (token: { value: string }) => {
    void sendToken(token?.value);
  });
  await plugin.addListener('registrationError', (err: any) => {
    // Simulator, missing entitlement, no network at launch. Not fatal: the next
    // cold start tries again.
    console.warn('[push] registration failed:', err?.error ?? err);
  });
}

async function sendToken(token: string | undefined): Promise<void> {
  if (!token) return;
  try {
    const info = await CapApp.getInfo().catch(() => null);
    await devicesApi.register({
      token,
      platform: nativePlatform() === 'android' ? 'android' : 'ios',
      appVersion: info?.version,
      build: info?.build,
      locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
    });
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* private mode — sign-out just has nothing to unregister */
    }
  } catch (err) {
    // A failed registration must never break the screen the user is on; the
    // next cold start retries.
    console.warn('[push] could not register device:', err);
  }
}

/**
 * Ask for permission, then register. Returns the resulting permission state so
 * the caller can show "Turn it on in iOS Settings" for a denial.
 */
export async function enablePush(): Promise<PushState> {
  const plugin = pushPlugin();
  if (!plugin) return 'unsupported';
  try {
    const result = await plugin.requestPermissions();
    const state = (result?.receive as PushPermission) ?? 'denied';
    if (state !== 'granted') return state;
    await attachRegistrationListeners(plugin);
    await plugin.register();
    return 'granted';
  } catch (err) {
    console.warn('[push] enable failed:', err);
    return 'denied';
  }
}

/**
 * Cold start: re-register when permission is already granted. Tokens rotate, so
 * skipping this quietly stops notifications after a restore or an OS upgrade.
 */
export async function registerIfGranted(): Promise<void> {
  const plugin = pushPlugin();
  if (!plugin) return;
  if (registrationInFlight) return void registrationInFlight;
  registrationInFlight = (async () => {
    try {
      const { receive } = await plugin.checkPermissions();
      if (receive !== 'granted') return null;
      await attachRegistrationListeners(plugin);
      await plugin.register();
      return null;
    } catch (err) {
      console.warn('[push] cold-start registration failed:', err);
      return null;
    } finally {
      registrationInFlight = null;
    }
  })();
  await registrationInFlight;
}

/**
 * Sign-out. Drops the server-side registration so the next person to sign in on
 * this phone does not inherit the previous account's notifications.
 */
export async function disablePush(): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
  if (!token) return;
  try {
    await devicesApi.unregister(token);
  } catch {
    // Signing out must not hang on a network call; the token is disabled server
    // side the first time APNs reports it gone anyway.
  }
}

/** The token this device last registered, if any. Used to mark "This device". */
export function currentPushToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export interface PushListeners {
  /** Arrived while the app is in the foreground — iOS shows nothing by itself. */
  onReceived?: (payload: PushNotificationPayload) => void;
  /** The user tapped the notification (or an action on it). */
  onAction?: (payload: PushNotificationPayload) => void;
}

/** Foreground + tap listeners. Returns an unsubscribe for React cleanup. */
export function addPushListeners(listeners: PushListeners): Unsubscribe {
  const plugin = pushPlugin();
  if (!plugin) return () => undefined;

  const handles: Promise<any>[] = [];

  if (listeners.onReceived) {
    handles.push(
      plugin.addListener('pushNotificationReceived', (notification: any) => {
        listeners.onReceived?.({
          title: notification?.title,
          body: notification?.body,
          data: notification?.data ?? {},
        });
      }),
    );
  }

  if (listeners.onAction) {
    handles.push(
      plugin.addListener('pushNotificationActionPerformed', (action: any) => {
        const notification = action?.notification ?? {};
        listeners.onAction?.({
          title: notification.title,
          body: notification.body,
          data: notification.data ?? {},
        });
      }),
    );
  }

  return () => {
    for (const handle of handles) {
      Promise.resolve(handle)
        .then((h) => h?.remove?.())
        .catch(() => undefined);
    }
  };
}

/**
 * The in-app path a notification wants to open.
 *
 * APNs sends it as `url` next to `aps` (see apps/api/src/notification/apns.service.ts),
 * and it is always relative — an absolute URL here would bounce the user out to
 * Safari, so anything that is not a path is ignored.
 */
export function notificationPath(payload: PushNotificationPayload): string | null {
  const url = payload.data?.url;
  if (typeof url !== 'string') return null;
  const value = url.trim();
  return value.startsWith('/') && !value.startsWith('//') ? value : null;
}
